import { useEffect, useRef, useState } from 'react';

import FollowingIcon from '@/assets/all-icons/following-icon.jsx';
import useProtectAction from '@/hooks/useProtectAction';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import UserService from '@/services/user.service';
import BubbleBurst from '@/shared-components/effects/components/BubbleBurst';
import {
  followMutationCoordinator,
  getFollowStateCacheKey,
  isSuccessfulFollowResponse,
  readCachedFollowState,
  resolveFollowState,
} from '@/utils/followState';

import './styles/FollowButton.css';

export default function FollowButton({ userId, imFollowing, isFollowingMe, onFollowAction, className="", userName, userUsername, followeeType = "user", extraSmall=false, small=false, medium=false, large=false, showFollowingIcon=false }) {
  const { cacheHandler, state, stateHandler } = useStateHandler();
  const t = useTranslations('common.profile.follow_button');
  const cacheKey = getFollowStateCacheKey({ followeeType, userId });
  const [localFollowStatus, setLocalFollowStatus] = useState(() => resolveFollowState({
    cacheHandler,
    followeeType,
    userId,
    imFollowing,
  }));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBurst, setShowBurst] = useState(false);
  const burstTimeoutRef = useRef(null);
  const privateActionHandler = useProtectAction({ typeOfProtection: "USER_ACCOUNT_REQUIRED" });

  const followStatus = resolveFollowState({
    cacheHandler,
    followeeType,
    userId,
    imFollowing: typeof imFollowing === "boolean" ? imFollowing : localFollowStatus,
  });

  useEffect(() => {
    if (typeof imFollowing !== "boolean" || !cacheKey) {
      return;
    }
    setLocalFollowStatus(imFollowing);
    if (readCachedFollowState(cacheHandler, { followeeType, userId }) === null) {
      cacheHandler.storeInCache(cacheKey, imFollowing, { type: "@cache/no-expiration" });
    }
  }, [cacheHandler, cacheKey, followeeType, imFollowing, userId]);

  useEffect(() => () => {
    if (burstTimeoutRef.current) {
      clearTimeout(burstTimeoutRef.current);
    }
  }, []);

  function updateFollowState(nextFollowStatus) {
    cacheHandler.storeInCache(cacheKey, nextFollowStatus, { type: "@cache/no-expiration" });
    setLocalFollowStatus(nextFollowStatus);
    onFollowAction?.(nextFollowStatus);
    cacheHandler.triggerAction("FOLLOW_ACTION");
    cacheHandler.triggerAction(`FOLLOW_ACTION_ON_${followeeType}.${userId}`);
  }

  async function handleFollowAction() {
    if (followStatus === null || !cacheKey || isSubmitting) {
      return;
    }

    const previousFollowStatus = followStatus;
    const nextFollowStatus = !previousFollowStatus;
    const mutationId = followMutationCoordinator.begin(cacheKey);

    setIsSubmitting(true);
    updateFollowState(nextFollowStatus);

    if (nextFollowStatus) {
      setShowBurst(true);
      if (burstTimeoutRef.current) clearTimeout(burstTimeoutRef.current);
      burstTimeoutRef.current = setTimeout(() => setShowBurst(false), 1300);
    }

    try {
      const result = await (nextFollowStatus
        ? UserService.followUser(userId, followeeType)
        : UserService.unfollowUser(userId, followeeType));

      if (!isSuccessfulFollowResponse(result)) {
        throw new Error("Unable to update follow status");
      }
    } catch (error) {
      if (followMutationCoordinator.isCurrent(cacheKey, mutationId)) {
        updateFollowState(previousFollowStatus);
      }
    } finally {
      followMutationCoordinator.complete(cacheKey, mutationId);
      setIsSubmitting(false);
    }
  }

  function handleFollowClick(event) {
    event.stopPropagation();
    const followTarget = userName || userUsername;
    stateHandler.set("unregisteredUser", ["guest_follow", followTarget]);
    privateActionHandler(handleFollowAction);
  }

  /** This prevents the user from following himself */
  if(String(state.user_session?._id || "") === String(userId || "")) {
    return <></>;
  }

  if (followStatus === null) {
    return <span className="follow-button__placeholder" aria-hidden="true" />;
  }

  const buttonClasses = ['text-custom__component', 'follow-button__button'];

  if(className) {
    buttonClasses.push(...className.split(' ').filter(Boolean));
  }

  if(followStatus === true) {
    buttonClasses.push('fill');
  }

  if (extraSmall) buttonClasses.push('extra-small');
  if (small) buttonClasses.push('small');
  if (medium) buttonClasses.push('medium');
  if (large) buttonClasses.push('large');

  const followText = t('follow');
  const followBackText = t('follow_back');
  const followingText = t('following');

  const resolvedFollowText = followText === 'follow' ? 'Follow' : followText;
  const resolvedFollowBackText = followBackText === 'follow_back' ? 'Follow back' : followBackText;
  const resolvedFollowingText = followingText === 'following' ? 'Following' : followingText;

  const followLabel = followStatus === true ?
    resolvedFollowingText
    : isFollowingMe === true ?
      resolvedFollowBackText
      :
      resolvedFollowText;

  const shouldShowIcon = showFollowingIcon && followStatus === true;

  return (
    <BubbleBurst active={showBurst}>
      <button
        type="button"
        className={buttonClasses.join(' ')}
        onClick={handleFollowClick}
        disabled={isSubmitting}
        aria-label={followLabel}
        aria-busy={isSubmitting}
        aria-pressed={followStatus === true}
      >
        {shouldShowIcon && (
          <FollowingIcon className="follow-button__following-icon" />
        )}
        <span className="follow-button__label">{followLabel}</span>
      </button>
    </BubbleBurst>
  );
}
